'use client';

import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { getSupportConfig } from '../lib/support-config';

interface ScheduleCallEmbedProps {
  className?: string;
}

interface CalNamespace {
  (action: string, options?: unknown): void;
  q?: unknown[];
  loaded?: boolean;
  ns?: Record<string, CalNamespace>;
}

declare global {
  interface Window {
    Cal?: CalNamespace;
  }
}

export function ScheduleCallEmbed({ className }: ScheduleCallEmbedProps) {
  const config = getSupportConfig();
  const ref = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!config.calComUsername) return;

    const calLink = `${config.calComUsername}/${config.calComEvent ?? 'support'}`;

    if (typeof window !== 'undefined' && !window.Cal) {
      const script = document.createElement('script');
      script.src = 'https://app.cal.com/embed/embed.js';
      script.async = true;
      script.onload = () => bootstrap(calLink);
      document.head.appendChild(script);
    } else {
      bootstrap(calLink);
    }

    function bootstrap(link: string) {
      const Cal = window.Cal;
      if (!Cal) return;
      Cal('init', { origin: 'https://cal.com' });
      Cal('inline', {
        elementOrSelector: ref.current,
        calLink: link,
        config: { layout: 'month_view', theme: 'auto' },
      });
      Cal('ui', {
        theme: 'auto',
        styles: { branding: { brandColor: '#f59e0b' } },
        hideEventTypeDetails: false,
      });
      setReady(true);
    }
  }, [config.calComUsername, config.calComEvent]);

  if (!config.calComUsername) {
    return (
      <div className="rounded-xl border border-dashed border-neutral-300 p-6 text-center text-sm text-neutral-500 dark:border-neutral-700 dark:text-neutral-400">
        Programarea de apeluri nu este configurată momentan.
      </div>
    );
  }

  return (
    <div className={className}>
      <div
        ref={ref}
        className="min-h-[520px] w-full overflow-hidden rounded-xl border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-900"
      />
      {!ready && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-neutral-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      )}
    </div>
  );
}
